import { BrandNameResolver, defaultBrandNameResolver } from './analyticsBrandName';
import {
  BugoRegionFilters,
  BugoRegionFuneralHomeRow,
  BugoRegionMonthRow,
  BugoRegionResult,
  BugoRegionSidoRow,
  BugoRegionSigunguRow,
  BugoRegionStatRow,
} from './adminBugoRegion.types';
import { BugoStatFevent, ShareFilter } from './adminBugoStat.types';
import { sidoLabelOf } from './koreaMap/koreaRegion';

/**
 * 지역별 통계 — 서버 행(상조×장례식장×월×조의글설정)을 시도/시군구/장례식장/월로 접는다.
 * 순수 함수만. 지도 도형은 import하지 않는다(번들 분리).
 */

/** 부고 탭 `brandKeyOf`와 같은 규칙 — `${brandId}#${index ?? ''}` */
export const regionBrandKeyOf = (row: BugoRegionStatRow): string =>
  `${row.bugoBrandId ?? ''}#${
    typeof row.bugoBrandAgencyIndex === 'number' ? row.bugoBrandAgencyIndex : ''
  }`;

/** 장례식장 묶음 키 — ref가 없는 옛 문서는 embed 이름으로 묶는다 */
export const funeralHomeKeyOf = (row: {
  funeralHomeInfoId: string | null;
  funeralHomeName: string;
}): string => row.funeralHomeInfoId ?? `embed:${row.funeralHomeName}`;

/** 공유 필터를 건수로 바꾼다 — 행에는 공유/전체만 있으므로 미공유는 차로 만든다 */
export const countOf = (row: BugoRegionStatRow, share: ShareFilter): number => {
  switch (share) {
    case 'shared':
      return row.sharedFeventCount;
    case 'unshared':
      return Math.max(0, row.feventCount - row.sharedFeventCount);
    default:
      return row.feventCount;
  }
};

/** 시군구 표기. 시군구를 못 알아본 묶음은 '(시군구 미상)', 세종은 시군구가 없다 */
export const sigunguLabelOf = (sido: string, sigungu: string): string => {
  if (sigungu) return sigungu;
  if (sido === '세종') return '세종시 전체';
  return '(시군구 미상)';
};

/** 조회 구간에 걸친 달 목록(YYYY-MM, 오름차순). 문자열 산술 — Date로 하면 시간대·말일에서 밀린다 */
export const monthsBetween = (from: string, to: string): string[] => {
  const start = from.slice(0, 7);
  const end = to.slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(start) || !/^\d{4}-\d{2}$/.test(end) || start > end) {
    return [];
  }
  const out: string[] = [];
  let [year, month] = start.split('-').map(Number) as [number, number];
  //* 상한 — 잘못된 입력으로 무한히 돌지 않게 (서버 상한 400일 ≈ 14달)
  for (let i = 0; i < 60; i += 1) {
    const ym = `${year}-${String(month).padStart(2, '0')}`;
    out.push(ym);
    if (ym >= end) break;
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return out;
};

const applyFilters = (
  rows: BugoRegionStatRow[],
  filters: BugoRegionFilters,
): BugoRegionStatRow[] => {
  const brandSet = new Set(filters.brandKeys);
  return rows.filter((row) => {
    if (brandSet.size > 0 && !brandSet.has(regionBrandKeyOf(row))) return false;
    if (filters.acqMsg === 'used' && !row.useAcqMsg) return false;
    if (filters.acqMsg === 'unused' && row.useAcqMsg) return false;
    return true;
  });
};

interface Bucket {
  feventCount: number;
  sharedFeventCount: number;
  funeralHomes: Set<string>;
}

const newBucket = (): Bucket => ({
  feventCount: 0,
  sharedFeventCount: 0,
  funeralHomes: new Set(),
});

const byCountDesc = <T extends { feventCount: number; label?: string; name?: string }>(
  a: T,
  b: T,
): number =>
  b.feventCount - a.feventCount ||
  (a.label ?? a.name ?? '').localeCompare(b.label ?? b.name ?? '');

export const aggregateBugoRegion = (
  rows: BugoRegionStatRow[],
  filters: BugoRegionFilters,
  range: { from: string; to: string },
  resolveBrandName: BrandNameResolver = defaultBrandNameResolver,
): BugoRegionResult => {
  const filtered = applyFilters(rows, filters);
  const { share } = filters;

  const sidoMap = new Map<string, Bucket & { sigungus: Set<string> }>();
  const sigunguMap = new Map<string, Bucket & { sido: string; sigungu: string }>();
  const fhMap = new Map<
    string,
    Omit<BugoRegionFuneralHomeRow, 'brandCount' | 'topBrandName' | 'topBrandCount'> & {
      brands: Map<string, number>;
    }
  >();
  const monthMap = new Map<string, BugoRegionMonthRow>();
  monthsBetween(range.from, range.to).forEach((ym) =>
    monthMap.set(ym, { ym, feventCount: 0, bySido: {} }),
  );

  let feventCount = 0;
  let sharedFeventCount = 0;
  let unresolvedFeventCount = 0;

  filtered.forEach((row) => {
    const count = countOf(row, share);
    if (count <= 0) return;
    //* 공유 필터를 걸면 공유 상가만 세므로 "공유 상가"는 count와 같아진다
    const shared = share === 'unshared' ? 0 : Math.min(row.sharedFeventCount, count);
    const fhKey = funeralHomeKeyOf(row);

    feventCount += count;
    sharedFeventCount += shared;
    if (!row.sido) unresolvedFeventCount += count;

    const sido = sidoMap.get(row.sido) ?? { ...newBucket(), sigungus: new Set<string>() };
    sido.feventCount += count;
    sido.sharedFeventCount += shared;
    sido.funeralHomes.add(fhKey);
    if (row.sigungu) sido.sigungus.add(row.sigungu);
    sidoMap.set(row.sido, sido);

    const sgKey = `${row.sido}|${row.sigungu}`;
    const sg = sigunguMap.get(sgKey) ?? {
      ...newBucket(),
      sido: row.sido,
      sigungu: row.sigungu,
    };
    sg.feventCount += count;
    sg.sharedFeventCount += shared;
    sg.funeralHomes.add(fhKey);
    sigunguMap.set(sgKey, sg);

    const fh = fhMap.get(fhKey) ?? {
      key: fhKey,
      funeralHomeInfoId: row.funeralHomeInfoId,
      name: row.funeralHomeName || '(이름 없음)',
      address: row.address,
      sido: row.sido,
      sigungu: row.sigungu,
      feventCount: 0,
      sharedFeventCount: 0,
      brands: new Map<string, number>(),
    };
    fh.feventCount += count;
    fh.sharedFeventCount += shared;
    const brandKey = regionBrandKeyOf(row);
    fh.brands.set(brandKey, (fh.brands.get(brandKey) ?? 0) + count);
    fhMap.set(fhKey, fh);

    //* 구간 밖 달(경계 시간대 차이)은 버리지 않고 끝에 붙인다 — 합계가 KPI와 어긋나면 안 된다
    const month = monthMap.get(row.ym) ?? { ym: row.ym, feventCount: 0, bySido: {} };
    month.feventCount += count;
    month.bySido[row.sido] = (month.bySido[row.sido] ?? 0) + count;
    monthMap.set(row.ym, month);
  });

  const sidos: BugoRegionSidoRow[] = Array.from(sidoMap.entries())
    .map(([key, bucket]) => ({
      key,
      label: sidoLabelOf(key),
      feventCount: bucket.feventCount,
      sharedFeventCount: bucket.sharedFeventCount,
      funeralHomeCount: bucket.funeralHomes.size,
      sigunguCount: bucket.sigungus.size,
    }))
    .sort(byCountDesc);

  const sigungus: BugoRegionSigunguRow[] = Array.from(sigunguMap.values())
    .map((bucket) => ({
      sido: bucket.sido,
      sigungu: bucket.sigungu,
      label: sigunguLabelOf(bucket.sido, bucket.sigungu),
      feventCount: bucket.feventCount,
      sharedFeventCount: bucket.sharedFeventCount,
      funeralHomeCount: bucket.funeralHomes.size,
    }))
    .sort(byCountDesc);

  const funeralHomes: BugoRegionFuneralHomeRow[] = Array.from(fhMap.values())
    .map(({ brands, ...rest }) => {
      let topKey = '';
      let topCount = 0;
      brands.forEach((value, key) => {
        if (value > topCount) {
          topCount = value;
          topKey = key;
        }
      });
      const [brandId, index] = topKey.split('#');
      return {
        ...rest,
        brandCount: brands.size,
        topBrandName: resolveBrandName(
          brandId || null,
          index ? Number(index) : undefined,
        ),
        topBrandCount: topCount,
      };
    })
    .sort(byCountDesc);

  const months = Array.from(monthMap.values()).sort((a, b) => a.ym.localeCompare(b.ym));

  return {
    totals: {
      feventCount,
      sharedFeventCount,
      funeralHomeCount: fhMap.size,
      sidoCount: sidos.filter((row) => row.key !== '').length,
      sigunguCount: sigungus.filter((row) => row.sigungu !== '').length,
      unresolvedFeventCount,
    },
    sidos,
    sigungus,
    funeralHomes,
    months,
  };
};

/**
 * 장례식장 드릴다운 — 부고 탭이 이미 받아 둔 원자료(fevent 목록)에서 그 장례식장 것만 고른다.
 * 서버 행에는 상가 목록이 없으니 원자료가 있을 때만 열린다.
 */
export const feventsOfFuneralHome = (
  fevents: BugoStatFevent[],
  funeralHomeKey: string,
): BugoStatFevent[] =>
  fevents.filter((fevent) => {
    const ref = fevent.funeralHomeInfo;
    const id = !ref ? null : typeof ref === 'string' ? ref : ref._id;
    if (id) return id === funeralHomeKey;
    const name = fevent.funeralHomeInfoEmbed?.name ?? '';
    return `embed:${name}` === funeralHomeKey;
  });
